import { useState } from 'react';
import axios from 'axios';
import PublicHeader from '../components/PublicHeader';
import { 
  Mail, 
  User, 
  BookOpen, 
  Calendar, 
  MapPin, 
  Clock,
  Download,
  FileText,
  Award,
  GraduationCap,
  ArrowLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentPortal() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);

  const handleLookup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/student/lookup`, { email });
      setStudentData(response.data);
      setStep('dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (registrationId, docType) => {
    try {
      const response = await axios.get(`${API}/documents/${docType}/${registrationId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${docType}_${registrationId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'En attente' },
      confirmed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Confirmée' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Annulée' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Terminée' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (step === 'email') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold gradient-text mb-2" data-testid="student-portal-title">Insitumed</h1>
            <p className="text-violet-600 text-sm">Espace Apprenant</p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 border border-violet-100">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-violet-100 rounded-full">
                <GraduationCap className="text-violet-600" size={48} />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">Accédez à votre espace</h2>
            <p className="text-sm text-gray-600 mb-6 text-center">
              Entrez votre email pour consulter vos formations et télécharger vos documents
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4" data-testid="student-error">
                {error}
              </div>
            )}

            <form onSubmit={handleLookup} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Votre email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-violet pl-10"
                    placeholder="votre@email.com"
                    required
                    data-testid="student-email-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-violet w-full"
                data-testid="student-submit-button"
              >
                {loading ? 'Vérification...' : 'Accéder à mon espace'}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-100 text-center">
              <a href="/catalogue" className="text-sm text-violet-600 hover:text-violet-700 flex items-center justify-center gap-2">
                <ArrowLeft size={16} />
                Retour au catalogue
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { learner, registrations, resource_orders: resourceOrders = [] } = studentData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50">
      <PublicHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 flex justify-end">
        <button
          onClick={() => {
            setStep('email');
            setEmail('');
            setStudentData(null);
          }}
          className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm"
          data-testid="student-logout"
        >
          Se déconnecter
        </button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-2xl p-8 text-white mb-8">
          <h2 className="text-3xl font-bold mb-2" data-testid="welcome-message">
            Bienvenue, {learner.first_name} ! 👋
          </h2>
          <p className="text-violet-100">
            Vous avez {registrations.length} inscription{registrations.length > 1 ? 's' : ''} à votre actif
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border border-violet-100 mb-8">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <User size={20} className="text-violet-500" />
            Mes informations
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500">Nom complet</p>
              <p className="font-semibold text-gray-800">{learner.first_name} {learner.last_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Email</p>
              <p className="font-semibold text-gray-800">{learner.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Téléphone</p>
              <p className="font-semibold text-gray-800">{learner.phone}</p>
            </div>
          </div>
        </div>

        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <BookOpen className="text-violet-500" />
          Mes formations
        </h3>

        {registrations.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-violet-100">
            <BookOpen className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 mb-4">Vous n'avez encore aucune formation</p>
            <a href="/catalogue" className="btn-violet inline-block">Voir le catalogue</a>
          </div>
        ) : (
          <div className="space-y-4">
            {registrations.map((reg) => (
              <div key={reg.id} className="bg-white rounded-xl p-6 shadow-md border border-violet-100" data-testid={`registration-${reg.id}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="md:col-span-2">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 bg-violet-100 rounded-lg">
                        <BookOpen className="text-violet-600" size={24} />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-800">{reg.training.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{reg.training.category}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar size={16} className="text-violet-500" />
                        <span>
                          {reg.training.start_date 
                            ? new Date(reg.training.start_date).toLocaleDateString('fr-FR')
                            : 'Date à confirmer'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock size={16} className="text-violet-500" />
                        <span>{reg.training.duration_hours}h</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <MapPin size={16} className="text-violet-500" />
                        <span className="truncate">{reg.training.location}</span>
                      </div>
                      <div>
                        {getStatusBadge(reg.status)}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Montant</p>
                      <p className="text-2xl font-bold text-violet-600">{reg.amount_paid?.toFixed(2)} €</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Paiement: {reg.payment_status === 'paid' ? '✅ Effectué' : '⏳ En attente'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-sm font-semibold text-gray-700 mb-3">📄 Mes documents</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => downloadDocument(reg.id, 'invoice')}
                      className="flex items-center gap-2 px-4 py-2 bg-violet-50 text-violet-600 rounded-lg hover:bg-violet-100 transition-colors text-sm"
                      data-testid={`download-invoice-${reg.id}`}
                    >
                      <FileText size={16} />
                      Facture
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => downloadDocument(reg.id, 'convocation')}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                      data-testid={`download-convocation-${reg.id}`}
                    >
                      <Mail size={16} />
                      Convocation
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => downloadDocument(reg.id, 'attestation')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm"
                      data-testid={`download-attestation-${reg.id}`}
                    >
                      <Award size={16} />
                      Attestation de présence
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => downloadDocument(reg.id, 'certificate')}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors text-sm"
                      data-testid={`download-certificate-${reg.id}`}
                    >
                      <GraduationCap size={16} />
                      Attestation de fin
                      <Download size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Ressources achetées */}
        <h3 className="text-2xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-2">
          <FileText className="text-violet-500" />
          Mes ressources
        </h3>

        {resourceOrders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-violet-100">
            <FileText className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 mb-4">Vous n'avez encore acheté aucune ressource</p>
            <a href="/ressources" className="btn-violet inline-block">Voir les ressources</a>
          </div>
        ) : (
          <div className="space-y-4">
            {resourceOrders.map((order) => (
              <div
                key={order.id}
                className="bg-white rounded-xl p-6 shadow-md border border-violet-100"
                data-testid={`resource-order-${order.id}`}
              >
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="p-2 bg-violet-100 rounded-lg">
                      <FileText className="text-violet-600" size={24} />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-800">{order.resource?.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {order.resource?.category} · Réf. {order.reference}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 mb-1">Montant</p>
                    <p className="text-xl font-bold text-violet-600">
                      {Number(order.amount).toFixed(2)} €
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                  {order.status === 'confirmed' ? (
                    <>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        ✅ Paiement reçu
                      </span>
                      {order.resource?.file_url && (
                        <a
                          href={order.resource.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors text-sm font-semibold"
                          data-testid={`download-resource-${order.id}`}
                        >
                          <Download size={16} />
                          Télécharger la ressource
                        </a>
                      )}
                    </>
                  ) : order.status === 'cancelled' ? (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      Annulée
                    </span>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 mr-2">
                        ⏳ Virement en attente
                      </span>
                      Votre ressource sera disponible dès la réception du virement.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
